import { Box, Button, Grid } from "@mui/material";
import {
  ConfirmFormModal,
  DrawStage,
  isRole,
  SnackBarMessage
} from "@toorak/tc-common-fe-sdk";
import React, { Suspense, useEffect, useRef, useState } from "react";
import HistoryIcon from "@mui/icons-material/HistoryOutlined";
import { useDispatch, useSelector, shallowEqual } from "react-redux";

import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { styled } from "@mui/material/styles";
import {
  hideLoader,
  showLoader
} from "../../../stores/loaderAndException/loaderAndException.action";
import { countDrawErrors } from "../DrawErrorCheck";
import {
  updateDrawDetails,
  postDrawStageHistory
} from "../../../stores/drawRequest/drawRequest.action";
import { StageAuditTrail } from "./StageAuditTrail";
import { DrawRolesEnum, showBudgetReconcile } from "../../../utils/constants";
import { RootState } from "../../../stores/rootReducer";
import { getRoles, isAMTView } from "../../AssetManagement.utils";
import {
  DrawRequestStore,
  drawsActionBtnInterface
} from "../../../stores/drawRequest/drawRequest.interface";
import { useDrawsStyles } from "../Draws.styles";
import {
  DontShowHistoryStages,
  OriginatorEditingStages,
  getStageBgColor,
  snackBarDetails,
  getColorBasedOnStatus
} from "../Constant";
import { DrawStageUpdatePopup } from "./DrawStageUpdatePopup";
import { updateDrawStage } from "../../../stores/drawRequest/drawRequest.reducer";
import { DrawDocsButtonsWrapper } from "../DrawDocTagging/DrawDocsButtonsWrapper";
import { getTasks, taskNames } from "../../../stores/tasks/Tasks.action";
import { budgetReviewStatusEnum } from "../../../ilp/list-view/BudgetTaskManagement";
import { HtmlWhiteTooltip } from "../../../utils/Tooltip";

const StyledInfoIcon = styled(InfoOutlinedIcon)({
  width: 15,
  height: 15,
  marginLeft: 5,
  marginTop: 2,
  color: "#32325d"
});

export enum popUpHeaderEnum {
  BudgetReviewConfirmation = "Budget Review Confirmation",
  UnSupervisedBudgetReview = "Unsupervised Budget",
  PrimaryBudgetNotAvailable = "Primary Budget Document not available",
  PreviousDrawReconcilePending = "Previous Draw Reconcile Pending",
  DrawWired = "Draw Wired"
}

const DrawMoreAction = React.lazy(() =>
  import("./DrawMoreAction").then((module) => ({
    default: module.DrawMoreAction
  }))
);

interface DrawDetailsHeaderInterface {
  drawId: string;
  isOrig: boolean;
  showDeleteDrawWarning: () => void;
  updateIsSubmitClicked: (val: boolean) => void;
  goBackToLoanListPage: () => void;
  updateIsEditing: (val: boolean) => void;
  isEditing: boolean;
  toorakLoanId: string;
}

export const DrawDetailsHeader = ({
  drawId,
  isOrig,
  showDeleteDrawWarning,
  updateIsSubmitClicked,
  goBackToLoanListPage,
  updateIsEditing,
  isEditing,
  toorakLoanId
}: DrawDetailsHeaderInterface) => {
  const { drawDetails, drawDocs, selectedBankDetails } = useSelector<
    RootState,
    DrawRequestStore
  >((state) => state.drawRequestStore, shallowEqual);
  const classes = useDrawsStyles();
  const dispatch = useDispatch();
  const [snackBarMsg, setSnackBarMsg] = useState<{
    messageType: "success" | "info" | "warning" | "error" | undefined;
    message: string;
  }>({
    messageType: undefined,
    message: ""
  });
  const [budgetReviewStatus, setBudgetReviewStatus] = useState<string>("");
  const [allDrawDocs, setAllDrawDocs] = useState<any>([]);
  const [budgetReconDisable, setBudgetReconDisable] = useState<boolean>(false);
  const [popUpInfo, setPopUpInfo] = useState<any>({
    popUpOpen: false,
    popUpBody: "",
    primaryBtnName: "",
    SecondaryBtnName: "",
    hidePrimaryBtn: false,
    hideSecondaryBtn: false,
    header: ""
  });
  const [showStatusUpdate, setShowStatusUpdate] = useState<boolean>(false);
  const [stageToUpdate, setStageToUpdate] = useState<DrawStage | null>(null);
  const [showStageHistory, setStageHistory] = useState<boolean>(false);
  const [showStageHistoryIcon, setShowStageHistoryIcon] =
    useState<boolean>(false);
  const lastBudgetDocsKeyRef = useRef<string>("");
  const { tasks } = useSelector<RootState, any>((state) => state.TasksStore);
  const drawDiscussionsHistory: any[] = useSelector<RootState, any>(
    (state) => state.drawRequestStore.drawDiscussions
  );

  useEffect(() => {
    if (!toorakLoanId) return;
    if (!tasks?.length) {
      getTasks(dispatch, {
        resourceTypes: ["LOAN"],
        resourceIds: [toorakLoanId],
        taskNames: [
          taskNames.TAG,
          taskNames.SPLIT,
          taskNames.REVIEW,
          taskNames.RECONCILE,
          taskNames.BUDGET_REVIEW,
          taskNames.BUDGET_APPROVE
        ]
      });
    }
  }, [toorakLoanId, tasks?.length]);

  function isPastDrawsBudgetReconciCompleted() {
    let pastBudgetReconciComplete = true;
    const allowedStatuses = ["Approved", "Approved Conditionally"];
    for (const pastDrawId of Object.keys(
      drawDiscussionsHistory
    ) as Array<string>) {
      if (drawId > pastDrawId) {
        if (
          !allowedStatuses?.includes(
            drawDiscussionsHistory[
              pastDrawId as keyof typeof drawDiscussionsHistory
            ]?.drawDetails?.inspectionReviewStatus
          )
        ) {
          pastBudgetReconciComplete = false;
          break;
        }
      }
    }
    return pastBudgetReconciComplete;
  }

  useEffect(() => {
    if (!drawDocs?.length) return;
    // Wait for tasks to load before proceeding
    if (!tasks?.length) {
      setBudgetReconDisable(true);
      return;
    }
    const budgetReviewStatus = tasks?.[0]?.assignedTasks.find((task: any) =>
      [taskNames.BUDGET_REVIEW].includes(task.taskName)
    )?.taskStatus;
    const filDocsList = drawDocs.filter(
      (item: any) =>
        item.isPurchasePrimary &&
        item.tags.some((tag: any) => tag.tag.code === "DDDC"|| tag.tag.code === "ADRF")
    );
    const docsKey = `${budgetReviewStatus ?? ""}|${filDocsList.length}`;
    if (lastBudgetDocsKeyRef.current === docsKey) return;
    lastBudgetDocsKeyRef.current = docsKey;
    setBudgetReviewStatus(budgetReviewStatus ?? "");
    setAllDrawDocs(filDocsList);
    setBudgetReconDisable(!filDocsList.length);
  // eslint-disable-next-line
  }, [drawDocs, tasks?.length]);

  const handleBudgeReconcileClick = (tabRedirect: any) => {
    const filteredDocsList = allDrawDocs.filter(
      (item: any) =>
        item.isPurchasePrimary &&
        item.tags.some((tag: any) => tag.tag.code === "DDDC"|| tag.tag.code === "ADRF")
    );
    const pastBudgetReconciComplete = isPastDrawsBudgetReconciCompleted();
    if (
      budgetReviewStatus === budgetReviewStatusEnum.Approved &&
      filteredDocsList.length
    ) {
    //   if (
    //     [
    //       wireTransferInitiated,
    //       WireUnsuccessful,
    //       wireStatusReview,
    //       wireCompleted
    //     ].includes(stage)
    //   ) {
    //     setPopUpInfo({
    //       popUpOpen: true,
    //       popUpBody: (
    //         <div style={{ paddingLeft: "21px", paddingRight: "40px" }}>
    //           Budget Reconciliation will be only read only as the draw is
    //           already in {stage}.
    //         </div>
    //       ),
    //       hidePrimaryBtn: false,
    //       hideSecondaryBtn: false,
    //       primaryBtnName: "Ok",
    //       SecondaryBtnName: "Cancel",
    //       header: popUpHeaderEnum.DrawWired
    //     });
    //     return;
    //   }
      tabRedirect("budgetReconciliation");
      return;
    }
    if (
      [
        budgetReviewStatusEnum.LoanSubmitted,
        budgetReviewStatusEnum.BudgetMissing
      ].includes(budgetReviewStatus as budgetReviewStatusEnum)
    ) {
      setPopUpInfo({
        popUpOpen: true,
        popUpBody: (
          <div style={{ paddingLeft: "21px", paddingRight: "40px" }}>
            Primary Budget Review Document not detected on the loan. Please mark
            a primary "Scope of Work" document for the loan.
          </div>
        ),
        hidePrimaryBtn: false,
        hideSecondaryBtn: false,
        SecondaryBtnName: "Cancel",
        primaryBtnName: "Go to Document Tagging",
        header: popUpHeaderEnum.PrimaryBudgetNotAvailable
      });
      return;
    }
    if (
      ![budgetReviewStatusEnum.Approved].includes(
        budgetReviewStatus as budgetReviewStatusEnum
      )
    ) {
      setPopUpInfo({
        popUpOpen: true,
        popUpBody: (
          <div style={{ paddingLeft: "21px", paddingRight: "40px" }}>
            Budget Review needs to be done first on the loan, do you want to
            proceed with Budget Review?
          </div>
        ),
        hidePrimaryBtn: false,
        hideSecondaryBtn: false,
        SecondaryBtnName: "Cancel",
        primaryBtnName: "Go to Budget Review",
        header: popUpHeaderEnum.BudgetReviewConfirmation
      });
    } else if (!pastBudgetReconciComplete) {
      setPopUpInfo({
        popUpOpen: true,
        popUpBody: (
          <div style={{ paddingLeft: "21px", paddingRight: "40px" }}>
            Budget Reconcile is not done on Previous Draws, Do you want to
            proceed?
          </div>
        ),
        hidePrimaryBtn: false,
        hideSecondaryBtn: false,
        SecondaryBtnName: "Cancel",
        primaryBtnName: "Proceed",
        header: "Previous Draw Reconcile Pending"
      });
    }
  };

  useEffect(() => {
    setShowStageHistoryIcon(
      !DontShowHistoryStages.includes(drawDetails?.stage)
    );
    // eslint-disable-next-line
  }, [drawDetails?.stage]);

  const role: DrawRolesEnum = getRoles();

  const {
    stage,
    fpiFlag,
    inspectionReviewStatus,
    budgetReconcileStatus,
    documentTaggingStatus
  } = drawDetails;

  const [openSnackBar, setOpenSnackBar] = useState<boolean>(false);
  const {
    draft,
    drawSubmitted,
    initialApproval,
    finalApproval,
    decline,
    wireTransferInitiated,
    WireUnsuccessful,
    wireCompleted,
    DrawInQueue,
    wireStatusReview
  } = DrawStage;

  const handleSaveClick = async () => {
    if (stage !== draft) {
      const errors = countDrawErrors(drawDetails, selectedBankDetails);
      if (errors) {
        updateIsSubmitClicked(true);
        return;
      }
    }
    const isEditingStage =
      [
        ...OriginatorEditingStages,
        draft,
        initialApproval,
        finalApproval,
        decline,
        WireUnsuccessful,
        wireStatusReview
      ].includes(drawDetails.stage);

    if (!(isEditingStage)) {
      return;
    }

    const updateStage: DrawStage = drawDetails.stage;
    try {
      await dispatch(
        updateDrawDetails(
          drawDetails,
          selectedBankDetails,
          drawId,
          isOrig,
          updateStage,
          drawDetails.originatorId
        )
      );
      updateIsEditing(false);
      setSnackBarMsg({
        messageType: "success",
        message: "Draw is successfully saved."
      });
      setOpenSnackBar(true);
    } catch (e) {
      setSnackBarMsg({
        messageType: "error",
        message: "Draw is not saved."
      });
      setOpenSnackBar(true);
    }
  };

  const handleSubmitClick = async () => {
    const errors = countDrawErrors(drawDetails, selectedBankDetails);
    Object.keys(selectedBankDetails).forEach((field) => {
      if (field === "routing" && selectedBankDetails[field]?.length != 9) {
        setSnackBarMsg({
          messageType: "warning",
          message: "Routing number must be of 9 digits"
        });
        setOpenSnackBar(true);
      }
    });
    if (errors) {
      console.error("field Errors count:", errors);
      updateIsSubmitClicked(true);
      return;
    }
    updateIsSubmitClicked(false);

    dispatch(showLoader());

    await dispatch(
      updateDrawDetails(
        drawDetails,
        selectedBankDetails,
        drawId,
        isOrig,
        drawSubmitted,
        drawDetails.originatorId
      )
    );
    dispatch(hideLoader());
    setSnackBarMsg({
      messageType: "success",
      message: "Draw is successfully submitted."
    });
    setOpenSnackBar(true);
  };

  const showSubmitButton = () =>
    stage !== undefined && stage.toUpperCase() === "DRAFT" && !isRole(DrawRolesEnum.ORIGINATOR_VIEW_DRAW);
  const showEditDrawButton = () =>
    isOrig
      ? OriginatorEditingStages.includes(stage) && !isRole(DrawRolesEnum.ORIGINATOR_VIEW_DRAW)
      : ![draft, wireTransferInitiated, wireCompleted].includes(stage) && isAMTView();
  const showDeleteDrawButton = () => {
    return stage !== undefined && [drawSubmitted, DrawInQueue, draft].includes(stage) && !isRole(DrawRolesEnum.ORIGINATOR_VIEW_DRAW);
  }

  const handleStageUpdateSuccess = (drawstage: DrawStage) => {
    dispatch(updateDrawStage(drawstage));
    setSnackBarMsg({
      messageType: "success",
      message: "Draw Status is successfully updated."
    });
    setOpenSnackBar(true);
  };

  const handleActionBtnClick = async (draw: drawsActionBtnInterface) => {
    if (
      [decline, wireTransferInitiated, wireStatusReview].includes(
        draw.title as DrawStage
      ) ||
      [wireTransferInitiated, wireCompleted].includes(stage)
    ) {
      // changing the status from wireTransferInitiated, wireCompleted to any other status OR
      // changing the status to decline, wireTransferInitiated
      // show updateComments pop up
      setStageToUpdate(draw.title as DrawStage);
      setShowStatusUpdate(true);
    } else {
      try {
        await dispatch(
          postDrawStageHistory(
            drawId,
            draw.title as DrawStage,
            role,
            drawDetails.originatorId
          )
        );
        handleStageUpdateSuccess(draw.title as DrawStage);
      } catch (e) {
        setSnackBarMsg({
          messageType: "error",
          message: "Draw Status is not updated."
        });
        setOpenSnackBar(true);
      }
    }
  };

  const handleConfirmStageUpdate = async (updateComments: string) => {
    if (!stageToUpdate) return;
    try {
      await dispatch(
        postDrawStageHistory(
          drawId,
          stageToUpdate,
          role,
          drawDetails.originatorId,
          updateComments
        )
      );
      handleStageUpdateSuccess(stageToUpdate);
    } catch (e) {
      setSnackBarMsg({
        messageType: "error",
        message: "Draw Status is not updated."
      });
      setOpenSnackBar(true);
    }
    setShowStatusUpdate(false);
  };

  const successHandler = () => {
    const { header } = popUpInfo;
    if (header === popUpHeaderEnum.BudgetReviewConfirmation) {
      window.open(
        `/internal/loan/createLoan/BridgeLoan/POST/${toorakLoanId}/budgetReview/v2`,
        "_blank"
      );
    } else if (header === popUpHeaderEnum.UnSupervisedBudgetReview) {
      window.open(
        `/drawDetails/${toorakLoanId}/${drawId}/budgetReconciliation`,
        "_blank"
      );
    } else if (header === popUpHeaderEnum.PrimaryBudgetNotAvailable) {
      window.open(
        `/internal/loan/createLoan/BridgeLoan/POST/${toorakLoanId}/documentReview?view=tag`,
        "_blank"
      );
    } else if (
      [
        popUpHeaderEnum.PreviousDrawReconcilePending,
        popUpHeaderEnum.DrawWired
      ].includes(header)
    ) {
      window.open(
        `/drawDetails/${toorakLoanId}/${drawId}/budgetReconciliation`,
        "_blank"
      );
    }

    setPopUpInfo({
      popUpOpen: false,
      popUpBody: "",
      primaryBtnName: "",
      SecondaryBtnName: "",
      hidePrimaryBtn: false,
      hideSecondaryBtn: false,
      header: ""
    });
  };

  return (
    <Grid item container xs={12} className={classes.headerContainer}>
      {isAMTView() && (
        <DrawStageUpdatePopup
          showStatusUpdate={showStatusUpdate}
          setShowStatusUpdate={setShowStatusUpdate}
          handleConfirmStageUpdate={handleConfirmStageUpdate}
        />
      )}
      <Suspense fallback={<span>Loading...</span>}>
        <ConfirmFormModal
          isOpen={popUpInfo.popUpOpen}
          handleClose={() => {
            setPopUpInfo({
              popUpOpen: false,
              popUpBody: "",
              primaryBtnName: "",
              SecondaryBtnName: "",
              hidePrimaryBtn: false,
              hideSecondaryBtn: false,
              header: ""
            });
          }}
          header={popUpInfo.header}
          body={popUpInfo.popUpBody}
          confirmCondition={false}
          primaryBtnName={popUpInfo.primaryBtnName}
          SecondaryBtnName={popUpInfo.SecondaryBtnName}
          hidePrimaryBtn={popUpInfo.hidePrimaryBtn}
          hideSecondaryBtn={popUpInfo.hideSecondaryBtn}
          size="lg"
          style={{ width: "670px !important" }}
          successHandler={successHandler}
          disableBackdropClick
        />
      </Suspense>
      <Grid item container style={{ width: "66%", minWidth: "802px" }}>
        <Box
          className="button-typography-wrapper-left"
          style={{ padding: "4px 0px" }}
        >
          <Button
            variant="contained"
            data-testid="back-button"
            color="primary"
            startIcon={<KeyboardArrowLeftIcon />}
            onClick={goBackToLoanListPage}
          >
            Back
          </Button>
        </Box>
        <span className={classes.drawHeaderDetails}>
          <span className={classes.itemHeaderText}>
            Draw Request ID: {drawId}
          </span>
          {/* change stage color here */}
          <span
            className={classes.stageDisplay}
            data-testid="draw-view-stageDisplay"
            style={{ backgroundColor: getStageBgColor(stage) }}
          >
            {stage?.toUpperCase()}
          </span>
          {!isOrig && (
            <span
              className={classes.stageDisplay}
              data-testid="draw-view-fpiFlagDisplay"
              style={{ backgroundColor: "#f3c316", color: "#172b4d" }}
            >
              {fpiFlag || "Non FPI"}
            </span>
          )}
          {showBudgetReconcile(stage) && isAMTView() && (
            <>
              <span
                className={classes.stageDisplay}
                data-testid="draw-view-stageDisplay"
                style={{
                  backgroundColor: getColorBasedOnStatus(documentTaggingStatus)
                }}
              >
                Document Tagging
                <HtmlWhiteTooltip title={documentTaggingStatus?.toUpperCase()}>
                  <StyledInfoIcon
                    style={{ verticalAlign: "middle", marginLeft: "5px" }}
                  />
                </HtmlWhiteTooltip>
              </span>
              <span
                className={classes.stageDisplay}
                data-testid="draw-view-stageDisplay"
                style={{
                  backgroundColor: getColorBasedOnStatus(budgetReconcileStatus)
                }}
              >
                Budget Reconcile
                <HtmlWhiteTooltip title={budgetReconcileStatus?.toUpperCase()}>
                  <StyledInfoIcon
                    style={{ verticalAlign: "middle", marginLeft: "5px" }}
                  />
                </HtmlWhiteTooltip>
              </span>
              <span
                className={classes.stageDisplay}
                data-testid="draw-view-stageDisplay"
                style={{
                  backgroundColor: getColorBasedOnStatus(inspectionReviewStatus)
                }}
              >
                Inspection Review
                <HtmlWhiteTooltip title={inspectionReviewStatus?.toUpperCase()}>
                  <StyledInfoIcon
                    style={{ verticalAlign: "middle", marginLeft: "5px" }}
                  />
                </HtmlWhiteTooltip>
              </span>
            </>
          )}

          <span>
            {showStageHistoryIcon && (
              <Button
                data-testid="draw-view-loanHistoryBtn"
                startIcon={<HistoryIcon fontSize="large" />}
                onMouseEnter={() => {
                  setStageHistory(!showStageHistory);
                }}
                onMouseLeave={() => {
                  setStageHistory(!showStageHistory);
                }}
              />
            )}
          </span>
          {showStageHistory && <StageAuditTrail />}
        </span>
      </Grid>
      <Grid
        item
        container
        style={{ justifyContent: "flex-end", width: "34%", minWidth: "555px", position: "absolute", right: 5 }}
      >
        {showEditDrawButton() && (
          <>
            {!isEditing ? (
              <Button
                onClick={() => {
                  updateIsEditing(true);
                }}
                data-testid="edit-button"
                className={classes.editButton}
              >
                Edit
              </Button>
            ) : (
              <Button
                variant="outlined"
                onClick={handleSaveClick}
                data-testid="save-button"
                className={classes.saveButton}
              >
                Save
              </Button>
            )}
          </>
        )}
        {showDeleteDrawButton() ? (
          <Button
            variant="text"
            data-testid="draw-delete-btn"
            className={`${classes.cancelButton} ${
              stage !== undefined && stage.toUpperCase() === "DRAFT"
                ? classes.cancelButton
                : classes.deleteDrawButton
            }`}
            onClick={() => showDeleteDrawWarning()}
          >
            Delete Draw
          </Button>
        ) : null}
        {showSubmitButton() && (
          <span>
            <Button
              variant="outlined"
              onClick={handleSaveClick}
              data-testid="save-button"
              className={classes.saveButton}
            >
              {" "}
              Save{" "}
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmitClick}
              data-testid="submit-button"
              className={classes.submitButton}
              color="info"
            >
              Submit
            </Button>
          </span>
        )}
        {isAMTView() && showBudgetReconcile(stage) && (
          <DrawDocsButtonsWrapper
            drawId={drawId}
            loanId={toorakLoanId}
            disable={!drawDocs?.length}
            handleBudgeReconcileClick={handleBudgeReconcileClick}
            budgetReconDisable={budgetReconDisable}
          />
        )}
        {isAMTView() &&
          ((showEditDrawButton() && !isEditing) || !showEditDrawButton()) && (
            <Suspense fallback={<span>Loading...</span>}>
              <DrawMoreAction
                handleActionBtnClick={handleActionBtnClick}
                drawDetails={drawDetails}
              />
            </Suspense>
          )}
      </Grid>
      <SnackBarMessage
        open={openSnackBar}
        verticalPlacement={snackBarDetails.verticalPlacement}
        horizontalPlacement={snackBarDetails.horizontalPlacement}
        autoHideDuration={snackBarDetails.autoHideDuration}
        handleClose={() => setOpenSnackBar(false)}
        messageType={snackBarMsg.messageType}
        message={snackBarMsg.message}
        data-testid="draw-update-message"
      />
    </Grid>
  );
};